import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, OverlayTrigger, Tooltip, Carousel } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBed, 
  faBath, 
  faRuler, 
  faHeart, 
  faMapMarkerAlt, 
  faCalendarAlt,
  faCoins,
  faEye,
  faChevronLeft,
  faChevronRight,
  faHome
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import '../style.css';

interface PropertyCardProps {
  property: any;
  onViewDetails: (property: any) => void;
  isSaved?: boolean;
  onSaveToggle?: (property: any) => void;
}

const PropertyCardEnhanced: React.FC<PropertyCardProps> = ({ property, onViewDetails, isSaved = false, onSaveToggle }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [autoRotateInterval, setAutoRotateInterval] = useState<NodeJS.Timeout | null>(null);
  const [isPropertySaved, setIsPropertySaved] = useState(isSaved);
  
  // Handle nested property_data structure
  const data = property.property_data || property;
  
  // Extract images
  const images = data.images || property.images || [];
  const hasMultipleImages = images.length > 1;
  
  // Calculate actual days on market
  const getDaysOnMarket = () => {
    if (data.daysOnMarket !== undefined && data.daysOnMarket !== null) {
      return data.daysOnMarket;
    }
    
    // If daysOnMarket is not provided, calculate from listing date
    if (data.listingDate) {
      const listingDate = new Date(data.listingDate);
      const today = new Date();
      const diffTime = Math.abs(today.getTime() - listingDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    
    return null;
  };
  
  const daysOnMarket = getDaysOnMarket();
  
  // Format price
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };
  
  // Get property image with fallback
  const getPropertyImage = (index: number = 0) => {
    if (images.length > index) {
      return images[index];
    }
    
    // Generate placeholder based on property characteristics
    const propertyType = data.propertyType || property.property_type || 'property';
    const price = data.price || property.price || 0;
    
    if (index === 0) {
      if (propertyType.includes('single') || propertyType === 'single_family') {
        return price > 1000000 
          ? `https://source.unsplash.com/400x300/?luxury,house`
          : `https://source.unsplash.com/400x300/?house,home`;
      } else if (propertyType.includes('condo') || propertyType.includes('town')) {
        return `https://source.unsplash.com/400x300/?apartment,condo`;
      } else if (propertyType.includes('land')) {
        return `https://source.unsplash.com/400x300/?land,property`;
      }
    }
    
    return `https://source.unsplash.com/400x300/?realestate,arizona&sig=${index}`;
  };
  
  // Handle image navigation
  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };
  
  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };
  
  // Auto-rotate images on hover
  useEffect(() => {
    if (isHovered && hasMultipleImages) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
      }, 2000); // Change image every 2 seconds
      
      setAutoRotateInterval(interval);
      
      return () => {
        clearInterval(interval);
      };
    } else {
      if (autoRotateInterval) {
        clearInterval(autoRotateInterval);
        setAutoRotateInterval(null);
      }
    }
  }, [isHovered, hasMultipleImages, images.length]);
  
  return (
    <Card 
      className="property-search-card h-100 shadow-sm hover-shadow transition"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setCurrentImageIndex(0); // Reset to first image
      }}
    >
      <div className="position-relative property-image-container">
        <Card.Img 
          variant="top" 
          src={getPropertyImage(currentImageIndex)}
          style={{ height: '250px', objectFit: 'cover' }}
          onError={(e) => {
            (e.target as HTMLImageElement).src = 
              `https://via.placeholder.com/400x300/e9ecef/6c757d?text=No+Image`;
          }}
        />
        
        {/* Image carousel controls */}
        {hasMultipleImages && (
          <>
            <button
              className="carousel-control-custom carousel-control-prev"
              onClick={handlePrevImage}
              type="button"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              className="carousel-control-custom carousel-control-next"
              onClick={handleNextImage}
              type="button"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
            
            {/* Image indicators */}
            <div className="carousel-indicators-custom">
              {images.map((_: string, index: number) => (
                <span
                  key={index}
                  className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                />
              ))}
            </div>
          </>
        )}
        
        {/* Save button */}
        <div className="position-absolute top-0 end-0 m-2">
          <OverlayTrigger
            placement="left"
            overlay={<Tooltip>{isPropertySaved ? 'Remove from favorites' : 'Save property'}</Tooltip>}
          >
            <div
              className="cursor-pointer hover-scale"
              onClick={(e) => {
                e.stopPropagation();
                setIsPropertySaved(!isPropertySaved);
                if (onSaveToggle) {
                  onSaveToggle(data);
                }
              }}
            >
              <FontAwesomeIcon 
                icon={faHeart} 
                className={`bg-dark bg-opacity-75 rounded-circle p-2 ${isPropertySaved ? 'text-danger' : 'text-white'}`}
                style={{ fontSize: '1.2rem' }}
              />
            </div>
          </OverlayTrigger>
        </div>
        
        {/* Property badges */}
        <div className="position-absolute bottom-0 start-0 m-2">
          <Badge bg="primary">{data.propertyType || property.property_type}</Badge>
          {data.pool && <Badge bg="info" className="ms-1">Pool</Badge>}
          {data.garage && data.garage >= 3 && (
            <Badge bg="secondary" className="ms-1">{data.garage} Car Garage</Badge>
          )}
        </div>
        
        {/* Status badges */}
        {(data.status === 'Active' || data.status === 'active' || property.status === 'active') && (
          <div className="position-absolute top-0 start-0 m-2">
            <Badge bg="success">Active</Badge>
          </div>
        )}
        {daysOnMarket !== null && daysOnMarket <= 7 && (
          <div className="position-absolute top-0 start-0 m-2" style={{ marginTop: '35px' }}>
            <Badge bg="danger">New</Badge>
          </div>
        )}
      </div>
      
      <Card.Body className="d-flex flex-column">
        <div className="mb-3">
          <h5 className="text-primary mb-1">
            {formatPrice(data.price || property.price)}
          </h5>
          <p className="text-muted mb-2">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
            {typeof data.address === 'string' 
              ? data.address 
              : data.address?.street || property.address || 'Address Not Available'}
          </p>
          <p className="text-muted small mb-0">
            {typeof data.address === 'object' && data.address?.city 
              ? `${data.address.city}, ${data.address.state} ${data.address.zip}`
              : `${data.city || property.city}, ${data.state || property.state} ${data.zipCode || data.zip || property.zip_code || ''}`}
          </p>
        </div>
        
        <div className="property-features d-flex justify-content-between text-muted small mb-3">
          <span>
            <FontAwesomeIcon icon={faBed} className="me-1" />
            {data.bedrooms || property.bedrooms || 0} Beds
          </span>
          <span>
            <FontAwesomeIcon icon={faBath} className="me-1" />
            {data.bathrooms || property.bathrooms || 0} Baths
          </span>
          <span>
            <FontAwesomeIcon icon={faRuler} className="me-1" />
            {(data.squareFeet || data.sqft || property.square_feet)?.toLocaleString() || 'N/A'} sq ft
          </span>
        </div>

        {data.yearBuilt && (
          <div className="text-muted small mb-2">
            <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
            Built in {data.yearBuilt}
            {data.hoaFee !== undefined && data.hoaFee > 0 && (
              <span className="ms-3">
                <FontAwesomeIcon icon={faCoins} className="me-1" />
                HOA: ${data.hoaFee}/mo
              </span>
            )}
          </div>
        )}

        {data.listingAgent && (
          <div className="border-top pt-2 mb-2">
            <small className="text-muted">
              Courtesy of {data.listingAgent.officeName || data.listingAgent.name || 'Listing Agent'}
            </small>
          </div>
        )}

        <div className="mt-auto d-flex justify-content-between align-items-center">
          <small className="text-muted">
            MLS# {data.mlsNumber || property.mls_number || ''}
            {daysOnMarket !== null && (
              <span className="ms-2">• {daysOnMarket} days</span>
            )}
          </small>
          <Button 
            size="sm" 
            variant="outline-primary"
            className="d-flex align-items-center gap-1"
            onClick={() => onViewDetails(data)}
          >
            <FontAwesomeIcon icon={faEye} />
            View
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default PropertyCardEnhanced;